import { useQuery } from "@tanstack/react-query";
import { format, parse } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import type { AssetBookingRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { isDefined } from "@/helpers/util";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { usePageEntryState } from "@/hooks/usePageEntryState";
import { useSlug } from "@/hooks/useSlug";
import { getMinutesOfDayFromTime, getTimeFromMinutesOfDay } from "@/modules/bookings/helpers";
import { bookableAssetMutations } from "@/queries/bookable-assets/mutations";
import { useBookableAssetsQueries } from "@/queries/bookable-assets/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { getMinBookingDate, getMaxBookingDate } from "./booking-dates";
import { BookingForm, type BookingFormProps, type CreateOrEditBookingFormValues } from "./BookingForm";

export function EditBookingPage(): React.ReactNode {
  const clockDate = useCurrentDate();
  // Keep the initial selection independent of the clock that refreshes booking limits.
  const [entryDate] = useState(() => new Date());
  const [date, setDate] = useState<Date>();

  const slug = useSlug();
  const { aid: assetId, bid: bookingId } = useParams<{ aid: string; bid: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const bookableAssetQueries = useBookableAssetsQueries();
  const {
    data: assetDetails,
    isPending: isPendingAssetDetails,
    error: errorAssetDetails,
  } = useQuery(
    bookableAssetQueries.details({
      assetId: assetId!,
    }),
  );
  const query = date ? { Date: format(date, "yyyy-MM-dd") } : undefined;
  const {
    data: assetSlots,
    isPending: isPendingAssetSlots,
    error: errorAssetSlots,
  } = useQuery(bookableAssetQueries.slots({ assetId: assetId!, query }));
  const {
    data: bookingDetails,
    isFetching: isFetchingBookingDetails,
    error: errorBookingDetails,
  } = useQuery({
    ...bookableAssetQueries.bookingDetails({ assetId: assetId!, bookingId: bookingId! }),
    enabled: isDefined(assetId) && isDefined(bookingId),
  });
  const { data: assetStatistics, error: assetStatisticsError } = useQuery({
    ...bookableAssetQueries.statistics({ assetId: assetId! }),
    enabled: assetDetails?.canDelete === true,
  });

  const deleteAsset = bookableAssetMutations.useDeleteAsset();
  const editBookingMutation = bookableAssetMutations.useEditBooking(assetId!, bookingId!);

  async function editBooking(data: CreateOrEditBookingFormValues) {
    const req: AssetBookingRequest = {
      date: format(data.date, "yyyy-MM-dd"),
      startTime: getTimeFromMinutesOfDay(data.startTime!),
      endTime: getTimeFromMinutesOfDay(data.endTime!),
      reason: data.reason,
    };

    return await editBookingMutation.mutateAsync(req);
  }

  const minBookingDate = getMinBookingDate(assetDetails, clockDate);
  const maxBookingDate = getMaxBookingDate(assetDetails, clockDate);
  const bookingDate = bookingDetails?.date;

  const defaultDate = useMemo(() => {
    if (!assetDetails) {
      // Should never be used (as we require asset details to be loaded before render), but just in case
      return entryDate;
    }

    if (bookingDate) {
      return parse(bookingDate, "yyyy-MM-dd", entryDate);
    }

    return getMinBookingDate(assetDetails, entryDate);
  }, [assetDetails, bookingDate, entryDate]);

  const defaultStartTime = useMemo(() => {
    if (bookingDetails) {
      return getMinutesOfDayFromTime(bookingDetails.startTime, "start");
    }

    return null;
  }, [bookingDetails]);
  const defaultEndTime = useMemo(() => {
    if (bookingDetails) {
      return getMinutesOfDayFromTime(bookingDetails.endTime, "end");
    }

    return null;
  }, [bookingDetails]);

  useEffect(() => {
    if (assetDetails?.id) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setDate(defaultDate);
    }
  }, [assetDetails?.id, defaultDate]);

  const error =
    !isDefined(assetId) ||
    !isDefined(bookingId) ||
    errorAssetDetails ||
    assetStatisticsError ||
    errorAssetSlots ||
    errorBookingDetails;
  const pageEntry = usePageEntryState({
    identity: `${assetId}/${bookingId}`,
    isFetching: isPendingAssetDetails || isFetchingBookingDetails,
    isReady: assetDetails !== undefined && bookingDetails !== undefined,
    error: error || undefined,
  });
  if (!isDefined(assetId) || !isDefined(bookingId) || pageEntry.error) {
    return <ErrorPage error={pageEntry.error || true} />;
  }

  const isLoading = pageEntry.isPending || !assetDetails || !bookingDetails;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  if (!bookingDetails.canEdit) {
    return (
      <DocumentPage tabTitle={assetDetails.name} title={assetDetails.name} breadcrumbs={null}>
        <Stack gap="md" align="start">
          <Notice
            variant="warning"
            message={t("page.bookings.edit-booking.unavailable")}
            data-testid="booking-edit-unavailable"
          />
          <Button
            type="link"
            href={routes.calendar.reservationDetails({ slug, aid: assetId, rid: bookingId })}
            data-testid="booking-edit-reservation-link"
          >
            {t("page.bookings.edit-booking.view-reservation")}
          </Button>
        </Stack>
      </DocumentPage>
    );
  }

  const formProps: BookingFormProps = {
    assetDetails,
    assetSlots: assetSlots?.slots || [],
    isLoadingSlots: isPendingAssetSlots,
    bookingDetails,
    defaultValues: {
      date: defaultDate,
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      reason: bookingDetails?.bookingReason,
      isRegulationAccepted: !!bookingDetails,
    },
    minDate: minBookingDate,
    maxDate: maxBookingDate,
    onDateChange: setDate,
    onSubmit: editBooking,
    isSubmitting: editBookingMutation.isPending || deleteAsset.isPending,
    futureBookings: assetStatistics?.futureBookingsCount ?? 0,
    onDelete: (payload) =>
      deleteAsset.mutateAsync(payload, {
        onSuccess: () => void navigate(routes.bookings.list({ slug })),
      }),
  };

  return <BookingForm {...formProps} />;
}
